import {
  Box, Grid,
} from '@mui/material';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import HeaderMenuSettingsModal from '../shared/HeaderMenuSettingsModal';
import BlockHeaderData from './BlockHeaderData';
import styles from './BlockHeader.module.css';
import HeaderMenu from '../shared/HeaderMenu';
import BlockProps from '../../../../models/BlockProps';
import BlockData from '../../../../models/BlockData';
import Utils from '../../../../utils/Utils';
import CloseButton from '../../../Buttons/Close';
import EditableText from '../../../Form/EditableText';
import LinkData from '../../../../models/LinkData';
import HamburgerMenuButton from '../../../Buttons/HamburgerMenuButton';
import useResponsive from '../../../../hooks/UseResponsive';

export interface BlockHeaderProps extends BlockProps {
  blockData: BlockData[]
}

function BlockHeader({
  data, isEditing, isTemplate, editHandler, blockData,
}: BlockHeaderProps) {
  const props = (data.data as BlockHeaderData);
  const hamburgerCheckbox = useRef(null);
  const [open, setOpen] = useState(false);

  useResponsive({
    items: [
      {
        wrapperId: 'wrapperId',
        targetId: 'hamburger',
        queries: [
          { size: 0, className: styles.flex },
          { size: 500, className: styles.hidden },
        ],
      },
      {
        wrapperId: 'wrapperId',
        targetId: 'hamburgerMenu',
        queries: [
          { size: 0, className: styles.flex },
          { size: 500, className: styles.hidden },
        ],
      },
      {
        wrapperId: 'wrapperId',
        targetId: 'menu',
        queries: [
          { size: 0, className: styles.hidden },
          { size: 500, className: styles.flex },
        ],
      },
    ],
  });

  const handleOpen = useCallback(() => {
    if (isEditing && !isTemplate) {
      setOpen(true);
    }
  }, [isEditing, isTemplate]);
  const handleClose = () => setOpen(false);

  const handleUpdate = (text: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const handleMenuDelete = useCallback(() => {
    props.menus = undefined;
    editHandler(data.id, props);
  }, [data.id, editHandler, props]);

  const hamburgerButton = useMemo(() => (
    <Grid id="hamburger" item xs="auto" justifyContent="end">
      {isEditing || isTemplate ? (
        <HamburgerMenuButton />
      ) : (
        <label htmlFor={styles['show-menu']}>
          <HamburgerMenuButton />
        </label>
      )}
    </Grid>
  ), [isEditing, isTemplate]);

  const hamburgerMenu = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return (
        <Grid
          id="hamburgerMenu"
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
          sx={{
            p: 1.5,
            backgroundColor: '#f5f7fb',
          }}
        >
          <Grid item xs={12} sx={{ width: '100%' }}>
            <HeaderMenu
              menus={props.menus}
              isEditing={isEditing}
              direction="column"
              handleMenuDelete={handleMenuDelete}
              handleOpen={handleOpen}
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  }, [handleMenuDelete, handleOpen, isEditing, props.menus]);

  const menuItems = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return (
        <Grid id="menu" container justifyContent="center" alignItems="center">
          <HeaderMenu
            menus={props.menus}
            isEditing={isEditing}
            handleMenuDelete={handleMenuDelete}
            handleOpen={handleOpen}
          />
        </Grid>
      );
    }
    return [];
  }, [props.menus, isEditing, handleOpen, handleMenuDelete]);

  useEffect(() => {
    if (isEditing) {
      hamburgerCheckbox.current.checked = false;
    }
  }, [isEditing]);

  return (
    <>
      <Grid
        container
        spacing={0}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className={styles.header}
        width="100%"
        id="wrapperId"
      >
        <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap" sx={{ p: 1.5 }}>
          {props.title ? (
            <Grid item sx={{ paddingRight: 2 }}>
              <Box sx={[Utils.hoverBorder(isEditing), { width: '100%' }]}>
                <CloseButton
                  show={isEditing}
                  updateHandler={() => {
                    props.title = undefined;
                    editHandler(data.id, props);
                  }}
                />
                <EditableText
                  text={props.title.value}
                  isEditable={isEditing && !isTemplate}
                  updateHandler={handleUpdate}
                />
              </Box>
            </Grid>
          ) : (
            <Grid item />
          )}

          <Grid item alignItems="center" justifyContent="end">
            {hamburgerButton}
            {menuItems}
          </Grid>
        </Grid>

        <input type="checkbox" id={styles['show-menu']} ref={hamburgerCheckbox} />
        <Box id={styles['hamburger-menu']} sx={{ width: '100%' }}>
          {hamburgerMenu}
          {!isEditing && (
            <Box className={styles.fadeDiv} />
          )}
        </Box>

        <HeaderMenuSettingsModal
          menuItems={props.menus ?? []}
          open={open}
          closeHandler={(newItems?: LinkData[]) => {
            if (newItems) {
              props.menus = newItems;
              editHandler(data.id, props);
            }
            handleClose();
          }}
          blockData={blockData}
        />

      </Grid>
      {!isEditing && (
        <Box className={styles.fadeDiv} />
      )}
    </>
  );
}

export default BlockHeader;
